/**
 * utils/userCriticalData.ts
 *
 * Utilitários para gerenciar dados críticos do usuário no localStorage
 * Mantém informações sobre aniversário e data de vencimento do CRECI
 *
 * Dados são atualizados durante o login e preservados durante logout
 */

export const USER_CRITICAL_DATA_KEY = "sellit-user-critical-data";
export const CRECI_CACHE_KEY = "sellit-creci-shown-date";
export const BIRTHDAY_CACHE_KEY = "sellit-birthday-shown-session";
export const CRECI_SESSION_KEY = "sellit-creci-shown-session";

export const CRECI_WARNING_STAGES = [15, 10, 5, 3, 0]; // Dias para notificação

export const PERSISTENT_STORAGE_KEYS = [
  USER_CRITICAL_DATA_KEY,
  CRECI_CACHE_KEY,
  BIRTHDAY_CACHE_KEY,
  "@token",
  "@token-expires-in",
];

export interface UserCriticalData {
  dataNascimento: string | null;
  dataVencimentoCreci: string | null;
  fazAniversarioHoje: boolean;
  lastUpdated: string;
}

/**
 * Carrega os dados críticos do usuário do localStorage
 * @returns UserCriticalData | null
 */
export function loadUserCriticalData(): UserCriticalData | null {
  try {
    const criticalDataStr = localStorage.getItem(USER_CRITICAL_DATA_KEY);
    if (criticalDataStr) {
      return JSON.parse(criticalDataStr) as UserCriticalData;
    }
  } catch (error) {
    console.error("[userCriticalData] Erro ao carregar dados críticos:", error);
    return null;
  }
  return null;
}

/**
 * Salva os dados críticos do usuário no localStorage
 * @param data UserCriticalData - dados a serem salvos
 * @returns boolean - true se salvou com sucesso
 */
export function saveUserCriticalData(data: UserCriticalData): boolean {
  try {
    // Verificar se houve mudança na data de vencimento do CRECI
    const currentData = loadUserCriticalData();
    if (
      currentData &&
      currentData.dataVencimentoCreci !== data.dataVencimentoCreci
    ) {
      // Se a data foi atualizada, resetar o aviso
      resetCreciWarning();
    }

    const dataWithTimestamp = {
      ...data,
      lastUpdated: data.lastUpdated || new Date().toISOString(),
    };

    localStorage.setItem(
      USER_CRITICAL_DATA_KEY,
      JSON.stringify(dataWithTimestamp)
    );

    dispatchUpdateEvent();

    return true;
  } catch (error) {
    console.error("[userCriticalData] Erro ao salvar dados críticos:", error);
    return false;
  }
}

/**
 * Despacha um evento customizado para notificar componentes sobre mudanças nos dados
 */
function dispatchUpdateEvent(): void {
  try {
    const event = new CustomEvent("user-data-updated");
    window.dispatchEvent(event);
  } catch (error) {
    console.error("[userCriticalData] Erro ao despachar evento:", error);
  }
}

/**
 * Verifica se o aviso de aniversário já foi mostrado nesta sessão
 * @returns boolean
 */
export function birthdayShownInSession(): boolean {
  try {
    return sessionStorage.getItem(BIRTHDAY_CACHE_KEY) === "true";
  } catch (error) {
    console.error(
      "[userCriticalData] Erro ao verificar sessão de aniversário:",
      error
    );
    return false;
  }
}

/**
 * Marca o aviso de aniversário como mostrado nesta sessão
 * @returns boolean
 */
export function markBirthdayAsShown(): boolean {
  try {
    sessionStorage.setItem(BIRTHDAY_CACHE_KEY, "true");
    return true;
  } catch (error) {
    console.error(
      "[userCriticalData] Erro ao marcar aniversário como mostrado:",
      error
    );
    return false;
  }
}

/**
 * Verifica se o aviso de CRECI já foi mostrado nesta sessão
 * @returns boolean
 */
export function creciShownInSession(): boolean {
  try {
    return sessionStorage.getItem(CRECI_SESSION_KEY) === "true";
  } catch (error) {
    console.error(
      "[userCriticalData] Erro ao verificar sessão de CRECI:",
      error
    );
    return false;
  }
}

/**
 * Marca o aviso de CRECI como mostrado nesta sessão
 * @returns boolean
 */
export function markCreciAsShown(): boolean {
  try {
    sessionStorage.setItem(CRECI_SESSION_KEY, "true");
    return true;
  } catch (error) {
    console.error(
      "[userCriticalData] Erro ao marcar CRECI como mostrado:",
      error
    );
    return false;
  }
}

/**
 * Preserva dados críticos durante o processo de logout
 * @returns Record<string, string> - dados preservados
 */
export function preserveCriticalDataForLogout(): Record<string, string> {
  const dataToPreserve: Record<string, string> = {};

  try {
    PERSISTENT_STORAGE_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        dataToPreserve[key] = value;
      }
    });
  } catch (error) {
    console.error(
      "[userCriticalData] Erro ao preservar dados para logout:",
      error
    );
  }

  return dataToPreserve;
}

/**
 * Restaura dados críticos após logout
 * @param dataToRestore Record<string, string> - dados a serem restaurados
 */
export function restoreCriticalDataAfterLogout(
  dataToRestore: Record<string, string>
): void {
  try {
    Object.entries(dataToRestore).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  } catch (error) {
    console.error(
      "[userCriticalData] Erro ao restaurar dados após logout:",
      error
    );
  }
}

/**
 * Reseta o aviso de CRECI (útil quando a data é atualizada após renovação)
 */
export function resetCreciWarning(): void {
  try {
    localStorage.removeItem(CRECI_CACHE_KEY);
  } catch (error) {
    console.error("[userCriticalData] Erro ao resetar aviso de CRECI:", error);
  }
}
